import axios from "axios";

// The React build is served by the FastAPI backend on the SAME origin in every
// environment (preview, *.emergent.host, and mapped custom domains alike), with
// all API routes under `/api`. We therefore call the backend on the CURRENT
// origin instead of a build-time value. This keeps the app working on whatever
// domain it is served from and makes it immune to a stale/incorrect baked-in
// REACT_APP_BACKEND_URL (e.g. a deploy slug that stops resolving once a custom
// domain is mapped). Falls back to the env var for non-browser contexts (build).
const BASE_URL =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || "");

export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    // CSRF defence-in-depth: any non-simple request to a state-changing
    // endpoint will carry this header, which a cross-origin attacker site
    // cannot set without triggering a CORS preflight (and our backend
    // refuses cross-origin preflights).
    "X-Requested-With": "XMLHttpRequest",
  },
});

// ---- Silent session renewal -------------------------------------------------
// The access-token cookie lasts 15 minutes; the refresh-token cookie lasts 7
// days. When any API call returns 401 we try POST /auth/refresh once (shared
// across concurrent 401s) and replay the original request. Without this, the
// page silently "freezes" after 15 minutes until the user logs out/in.
const AUTH_NO_RETRY = /\/auth\/(login|refresh|logout|2fa\/login|accept-invite|reset-password)/;
let refreshInFlight = null;

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !AUTH_NO_RETRY.test(original.url || "")
    ) {
      original._retried = true;
      try {
        refreshInFlight =
          refreshInFlight ||
          api.post("/auth/refresh").finally(() => {
            refreshInFlight = null;
          });
        await refreshInFlight;
        return api(original);
      } catch (refreshErr) {
        // Refresh failed (expired/invalid) → surface the original 401.
        console.debug("[auth] token refresh failed:", refreshErr?.message || refreshErr);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  const clean = (s) => String(s).replace(/^Value error,\s*/i, "");
  if (typeof detail === "string") return clean(detail);
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? clean(e.msg) : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return clean(detail.msg);
  return String(detail);
}

const INTL_LOCALE = {
  it: "it-IT",
  en: "en-US",
  no: "nb-NO",
  de: "de-DE",
};

function resolveIntlLocale(locale) {
  return INTL_LOCALE[locale] || INTL_LOCALE.it;
}

export function formatCurrencyEUR(value, locale = "it") {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(resolveIntlLocale(locale), {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
    useGrouping: "always",
  }).format(value);
}

export function formatNumber(value, fractionDigits = 2, locale = "it") {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(resolveIntlLocale(locale), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: "always",
  }).format(value);
}

export function formatDate(iso, locale = "it") {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(resolveIntlLocale(locale), { day: "2-digit", month: "short", year: "numeric" }).format(d);
  } catch {
    return String(iso);
  }
}
